import { isEmailMessage } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/email";
import type { ConversationThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { useHandler } from "@redotech/react-util/hook";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Option } from "effect";
import { memo, useMemo, useState } from "react";
import { useThreadMessages } from "../hooks/use-conversation-data";
import { useEmailThreadView } from "../hooks/use-email-thread-view";
import { useThreadDrafts } from "../hooks/use-thread-drafts";
import { EmailMessageCard } from "./email-message-card";
import { ThreadHeader } from "./thread-header";

/**
 * Email thread view component.
 * Fetches its own messages via useThreadMessages and uses useEmailThreadView
 * for display state management.
 */
export const EmailThreadView = memo(function EmailThreadView({
  thread,
}: {
  thread: ConversationThread;
}) {
  const { data: messages } = useThreadMessages(
    thread.conversationId,
    thread.id,
  );

  const { data: allDrafts } = useThreadDrafts(thread.id);

  // Filter drafts to this thread
  const threadDrafts = useMemo(
    () => (allDrafts ?? []).filter((d) => d.threadId === thread.id),
    [allDrafts, thread.id],
  );

  // Filter to email messages only
  const emailMessages = useMemo(() => {
    return messages.filter((m) => isEmailMessage(m));
  }, [messages]);

  const {
    formattedMessages,
    isMessageExpanded,
    toggleMessageExpanded,
    isReplyOpen,
    openReply,
    closeReply,
    getReplyMode,
  } = useEmailThreadView(emailMessages, threadDrafts);

  // Extract subject from thread channel data if available
  const threadSubject =
    thread.integrationType === "gmail" || thread.integrationType === "outlook"
      ? (thread.channelData?.subject ?? null)
      : null;

  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleCollapsed = useHandler(() => setIsCollapsed((prev) => !prev));

  if (formattedMessages.length === 0) {
    return (
      <Flex align="center" dir="column" gap="md" p="xl">
        <Text fontSize="md" textColor="secondary">
          No messages in this thread
        </Text>
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="none">
      <ThreadHeader
        channel={thread.channel}
        isCollapsed={isCollapsed}
        latestMessageTime={
          formattedMessages[formattedMessages.length - 1]?.timestamp
        }
        onClick={toggleCollapsed}
        subject={threadSubject}
      />
      {!isCollapsed &&
        formattedMessages.map((message, index) => {
          const draft = (allDrafts ?? []).find(
            (d) =>
              Option.getOrUndefined(d.inReplyToMessageId) === message.messageId,
          );
          return (
            //TODO: the email message card can hold most of this stuff instead of the parent component.
            // regardless, it should be refactored
            <EmailMessageCard
              channel={thread.channel}
              conversationId={thread.conversationId}
              initialDraft={draft ?? null}
              initialReplyMode={getReplyMode(message.messageId)}
              isExpanded={isMessageExpanded(message.id)}
              isLast={index === formattedMessages.length - 1}
              isReplyOpen={isReplyOpen(message.messageId)}
              key={message.id}
              message={message}
              onDiscardDraft={() => closeReply(message.messageId)}
              onForward={() => openReply(message.messageId, "forward")}
              onReply={() => openReply(message.messageId, "reply")}
              onReplyAll={() => openReply(message.messageId, "replyAll")}
              onToggleExpand={() => toggleMessageExpanded(message.id)}
            />
          );
        })}
    </Flex>
  );
});
